import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowLeft, Send, MessageSquare, Clock, CheckCircle } from 'lucide-react';
import { GridBackground } from '../components/TechUI';
import SEO from '../components/SEO';
import { toast } from 'react-hot-toast';

const ContactUsPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.message) {
            toast.error('Please fill in all required fields.');
            return;
        }

        setIsSubmitting(true);
        try {
            // Send via mailto fallback (can be replaced with API call later)
            const mailtoLink = `mailto:support@rastorai.com?subject=${encodeURIComponent(formData.subject || 'Contact Form Submission')}&body=${encodeURIComponent(
                `Name: ${formData.name}\nEmail: ${formData.email}\n\n${formData.message}`
            )}`;
            window.location.href = mailtoLink;
            setSubmitted(true);
            toast.success('Opening your email client...');
        } catch (err) {
            toast.error('Failed to submit. Please email us directly.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <GridBackground className="min-h-screen">
            <SEO
                title="Contact Us | Rastor AI"
                description="Get in touch with Rastor AI. Reach us via email, phone, or contact form for support, sales, or partnership inquiries."
            />
            <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <Link
                    to="/"
                    className="inline-flex items-center text-slate-400 hover:text-emerald-400 transition-colors font-mono text-xs uppercase tracking-wider mb-8"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
                </Link>

                <div className="flex items-center space-x-4 mb-8">
                    <div className="w-12 h-12 border border-emerald-500/50 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                        <MessageSquare className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-white font-mono">CONTACT US</h1>
                        <p className="text-slate-500 text-xs font-mono uppercase mt-1">We'd love to hear from you</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                    {/* Contact Info Cards */}
                    <div className="lg:col-span-2 space-y-4">
                        <div className="bg-[#0F131F]/60 border border-white/5 rounded-2xl p-6 backdrop-blur-sm hover:border-emerald-500/20 transition-colors">
                            <Mail className="w-8 h-8 text-emerald-400 mb-3" />
                            <h3 className="text-white font-bold font-mono text-sm mb-1">Email</h3>
                            <a href="mailto:support@rastorai.com" className="text-emerald-400 text-sm font-mono hover:underline block">
                                support@rastorai.com
                            </a>
                            <a href="mailto:sales@rastorai.com" className="text-slate-400 text-xs font-mono hover:text-emerald-400 transition-colors block mt-1">
                                sales@rastorai.com (Enterprise)
                            </a>
                        </div>

                        <div className="bg-[#0F131F]/60 border border-white/5 rounded-2xl p-6 backdrop-blur-sm hover:border-emerald-500/20 transition-colors">
                            <Phone className="w-8 h-8 text-emerald-400 mb-3" />
                            <h3 className="text-white font-bold font-mono text-sm mb-1">Phone</h3>
                            <a href="tel:+918130829610" className="text-emerald-400 text-sm font-mono hover:underline block">
                                +91 8130829610
                            </a>
                            <p className="text-slate-500 text-xs font-mono mt-1">Mon–Sat, 10 AM – 7 PM IST</p>
                        </div>

                        <div className="bg-[#0F131F]/60 border border-white/5 rounded-2xl p-6 backdrop-blur-sm hover:border-emerald-500/20 transition-colors">
                            <MapPin className="w-8 h-8 text-emerald-400 mb-3" />
                            <h3 className="text-white font-bold font-mono text-sm mb-1">Location</h3>
                            <p className="text-slate-400 text-sm font-mono">India</p>
                        </div>

                        <div className="bg-[#0F131F]/60 border border-white/5 rounded-2xl p-6 backdrop-blur-sm hover:border-emerald-500/20 transition-colors">
                            <Clock className="w-8 h-8 text-emerald-400 mb-3" />
                            <h3 className="text-white font-bold font-mono text-sm mb-1">Response Time</h3>
                            <p className="text-slate-400 text-sm font-mono">Within 24–48 hours</p>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="lg:col-span-3">
                        <div className="bg-[#0F131F]/60 border border-white/5 rounded-2xl p-8 backdrop-blur-sm">
                            {submitted ? (
                                <div className="text-center py-12">
                                    <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                                    <h3 className="text-xl font-bold text-white font-mono mb-2">MESSAGE SENT</h3>
                                    <p className="text-slate-400 text-sm font-mono mb-6">
                                        Thank you for reaching out! We'll get back to you within 24–48 hours.
                                    </p>
                                    <button
                                        onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', subject: '', message: '' }); }}
                                        className="text-emerald-400 text-sm font-mono hover:underline"
                                    >
                                        Send another message →
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <h3 className="text-lg font-bold text-white font-mono mb-4">SEND US A MESSAGE</h3>

                                    <div>
                                        <label className="block text-xs font-mono font-semibold text-slate-400 uppercase tracking-wider mb-2">
                                            Full Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => handleChange('name', e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-sm placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all"
                                            placeholder="Your name"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-mono font-semibold text-slate-400 uppercase tracking-wider mb-2">
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleChange('email', e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-sm placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all"
                                            placeholder="your@email.com"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-mono font-semibold text-slate-400 uppercase tracking-wider mb-2">
                                            Subject
                                        </label>
                                        <select
                                            value={formData.subject}
                                            onChange={(e) => handleChange('subject', e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-sm focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all appearance-none"
                                        >
                                            <option value="" className="bg-[#0F131F]">Select a topic...</option>
                                            <option value="General Inquiry" className="bg-[#0F131F]">General Inquiry</option>
                                            <option value="Subscription & Billing" className="bg-[#0F131F]">Subscription & Billing</option>
                                            <option value="Refund Request" className="bg-[#0F131F]">Refund Request</option>
                                            <option value="Technical Support" className="bg-[#0F131F]">Technical Support</option>
                                            <option value="Enterprise / B2B" className="bg-[#0F131F]">Enterprise / B2B</option>
                                            <option value="Partnership" className="bg-[#0F131F]">Partnership</option>
                                            <option value="Bug Report" className="bg-[#0F131F]">Bug Report</option>
                                            <option value="Feature Request" className="bg-[#0F131F]">Feature Request</option>
                                            <option value="Other" className="bg-[#0F131F]">Other</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-mono font-semibold text-slate-400 uppercase tracking-wider mb-2">
                                            Message *
                                        </label>
                                        <textarea
                                            value={formData.message}
                                            onChange={(e) => handleChange('message', e.target.value)}
                                            rows={5}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-sm placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all resize-none"
                                            placeholder="How can we help you?"
                                            required
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-mono font-bold rounded-xl hover:from-emerald-500 hover:to-emerald-400 transition-all shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Send className="w-4 h-4" />
                                        {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </GridBackground>
    );
};

export default ContactUsPage;
